import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import {
  Select, MenuItem, FormControl, InputLabel, Divider, SelectChangeEvent, Dialog,
  TextField, InputAdornment, IconButton,
  Skeleton,
} from '@mui/material';
import {
  getUrlPath, slugToString, toSlug, updateRouterQuery, slugToValues,
} from 'lib/utils/filterUtils';
import {
  pluralize,
  teamIsPaid,
  teamIsPaidFullTeamsPlan,
  userIsAdmin,
  userIsProOrAdmin,
  teamHasActiveExternalInvoice,
} from 'lib/helpers';
import { List } from 'lib/types';
import { useApplicationState } from 'lib/contexts/ApplicationState';
import { useTeams } from 'lib/contexts/teamsContext';
import { useUserLists } from 'lib/contexts/userListsContext';
import { useUpgradeModal } from 'components/UpgradeModal';
import { AddEditList } from 'components/AddEditList';
import { convertZuluDate } from 'lib/utils/datetime';
import { isGondolaApp } from 'lib/utils/mobileAppUtils';
import { FAVORITES_LIST_NAME } from 'lib/constants';
import { Team } from 'lib/types/teams';
import { Project } from 'lib/types/projects';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  /** If provided, we only show the objects that match the type */
  types?: ('team' | 'list' | 'project')[];
  /** If provided, we only show lists and projects that belong to this team */
  teamId?: number;
  label?: string;
  onListChange?: ({ teamId, list, project }: {
    teamId?: string | string[];
    list?: string | string[];
    project?: string | string[];
  }) => void;
  /** If true, we filter out some lists based on the user's subscription */
  isDashboard?: boolean;
}

interface SelectValue {
  type: 'team' | 'list' | 'project' | 'none';
  id: string;
  name: string;
}

/** Only show the filter if there are more than this many items */
const SHOW_FILTER_ITEMS_MIN = 10;

/**
 * A filter for the user's lists.
 *
 * This component is used so users can choose a list or team accounts on some views.
 * On the dashboard we use some logic that disallows certain lists from being visible
 * based on the user's subscription.
 *
 * @param onListChange - A callback function that is called when the user selects a list.
 */
export const UserListFilter = ({
  onListChange,
  label, isDashboard = false,
  types = ['team', 'list', 'project'],
  teamId,
}: Props) => {
  const router = useRouter();
  const { showUpgradeModal } = useUpgradeModal();
  const { currentUser, api, isAuthed } = useApplicationState();
  const teams = useTeams();
  const [searchQuery, setSearchQuery] = useState('');

  const [adding, setAdding] = useState(false);
  const { lists, setLists } = useUserLists();
  const [projects, setProjects] = useState<Project[]>([]);
  const hasFetchedProjects = useRef(false);

  /**
   * Filter the lists based on the user's subscription and teamId.
   *
   * If the user is not on a paid team, we show their private lists.
   * Always show job lists
   *
   * If the user is on a paid teams plan, we show all the lists.
   * If teamId is provided, only show lists that belong to that team.
   */
  const filterLists = (allLists: List[] | undefined) => {
    if (!currentUser?.id) return [];
    if (!allLists?.length) return [];

    // First filter by teamId if provided
    let filteredByTeam = allLists;
    if (teamId) {
      filteredByTeam = allLists.filter((l) => l.teamId === teamId);
    }

    if (!isDashboard || userIsAdmin(currentUser)) return filteredByTeam;

    const allowedLists: List[] = [];

    filteredByTeam.forEach((l) => {
      // All users can see their personal lists
      if (!l.teamId) {
        allowedLists.push(l);
      }

      // Any active team subscription (starter or teams) allows job lists
      if (l.jobId) {
        allowedLists.push(l);
      }

      // The active 'teams' plan users can see all lists
      if (teamIsPaidFullTeamsPlan(l.team)) {
        allowedLists.push(l);
      }
    });

    return allowedLists;
  };

  /**
   * Filter the teams based on the user's subscription.
   *
   * We show only the teams that have an active subscription of any kind
   * or have an active external invoice.
   */
  const filterTeams = (allTeams: Team[] | undefined) => {
    if (!allTeams) return [];
    if (!isDashboard || userIsAdmin(currentUser)) return allTeams;

    return allTeams.filter((t) => teamIsPaid(t) || teamHasActiveExternalInvoice(t));
  };

  const filteredLists = types.includes('list') ? filterLists(lists) : [];
  const filteredTeams = types.includes('team') ? filterTeams(teams) : [];

  const addList = (list: List) => {
    setLists([
      ...(lists || []),
      list,
    ]);
  };

  const parseSelectValue = (value: string): SelectValue => {
    if (!value) return { type: 'none', id: '', name: '' };

    if (value.startsWith('team-')) {
      const selectedTeamId = value.split('-')[1];
      const team = filteredTeams?.find((t) => t.id === Number(selectedTeamId));
      return { type: 'team', id: selectedTeamId, name: team?.name || '' };
    }

    const project = filteredProjects?.find((p) => p.publicId === value);
    if (project) {
      return { type: 'project', id: project.publicId || '', name: project.name || '' };
    }

    const list = filteredLists?.find((l) => l.publicId === value);
    return { type: 'list', id: value, name: list?.name || '' };
  };

  const onChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;

    if (value === 'manage' || value === 'add new') {
      e.target.value = selected || '';
      if (value === 'add new') openAddList();
      else router.push('/lists');
      return;
    }

    const selection = parseSelectValue(value);
    if (selection.type === 'none') {
      updateRouterQuery(router, {
        list: [],
        sort: [],
        project: [],
        showHidden: [],
        teamId: teamId ? teamId.toString() : [],
      });
      onListChange?.({});
      return;
    }

    const queryFromSelect = (() => {
      if (selection.type === 'team') {
        return {
          teamId: toSlug(selection.name, selection.id),
          list: [],
          project: [],
        };
      }
      if (selection.type === 'project') {
        return {
          project: toSlug(selection.name, selection.id),
          list: [],
          teamId: teamId ? teamId.toString() : [],
        };
      }
      return {
        list: toSlug(selection.name, selection.id),
        teamId: teamId ? teamId.toString() : [],
        project: [],
      };
    })();

    const query = {
      ...router.query,
      showHidden: [],
      from: [],
      ...queryFromSelect,
    };

    router.push({
      pathname: `/${getUrlPath(router.asPath)}`,
      query,
    }, undefined, { shallow: true });

    onListChange?.(queryFromSelect);
  };

  const openAddList = () => {
    if (!userIsProOrAdmin(currentUser)) {
      showUpgradeModal('Upgrade to create more lists');
      return;
    }

    setAdding(true);
  };

  const favoritesList = filteredLists?.filter((l) => l.name === FAVORITES_LIST_NAME)[0];

  let selected = '';
  if (router.query.list) {
    selected = slugToString(router.query.list as string) || '';
  } else if (router.query.teamId && !teamId) {
    // If teamId is provided, we don't want to use the router query
    selected = slugToString(router.query.teamId as string) || '';
  } else if (router.query.project) {
    selected = slugToString(router.query.project as string) || '';
  }

  useEffect(() => {
    const getProjects = async () => {
      const p = await api.getProjects();
      setProjects(p);
    };

    if (types.includes('project') && currentUser && api && !hasFetchedProjects.current) {
      hasFetchedProjects.current = true;
      getProjects();
    }
  }, [api, currentUser, types]);

  const filterItemsBySearch = (items: any[], searchText: string) => {
    if (!searchText) return items;
    const query = searchText.toLowerCase();
    return items.filter((item) => {
      if (item.name) return item.name.toLowerCase().includes(query);
      if (item.teamName) return item.teamName.toLowerCase().includes(query);
      return false;
    });
  };

  const filteredTeamsBySearch = filterItemsBySearch(filteredTeams || [], searchQuery);
  const filteredListsBySearch = filterItemsBySearch(
    filteredLists?.filter((l) => l.name !== FAVORITES_LIST_NAME) || [],
    searchQuery,
  );
  let filteredProjects: Project[] = [];
  if (types.includes('project')) {
    if (teamId) {
      filteredProjects = (projects || []).filter((p) => p.teamId === teamId);
    } else {
      filteredProjects = projects || [];
    }
  }
  const filteredProjectsBySearch = filterItemsBySearch(filteredProjects, searchQuery)
    .sort((a, b) => a.name.localeCompare(b.name));

  const totalItems = (filteredTeams?.length || 0)
    + (filteredLists?.filter((l) => l.name !== FAVORITES_LIST_NAME).length || 0)
    + (filteredProjects?.length || 0);

  const handleClearSelection = (e: any) => {
    e.stopPropagation();
    updateRouterQuery(router, {
      list: [],
      sort: [],
      project: [],
      showHidden: [],
      teamId: teamId ? teamId.toString() : [],
    });
    onListChange?.({});
  };

  if (!isAuthed) return <div><Skeleton variant="rectangular" height={30} width={300} /></div>;

  return (
    <div className="w-full">
      <Dialog
        open={adding}
        onClose={() => setAdding(false)}
        maxWidth="sm"
        fullWidth
      >
        <div className="p-5">
          <AddEditList lists={lists || []} onSave={addList} onCancel={() => setAdding(false)} />
        </div>
      </Dialog>
      <FormControl className="w-full relative" size="small">
        <InputLabel id="list-label">{label || 'Lists'}</InputLabel>
        <Select
          labelId="list-label"
          id="list-select"
          name="list-select"
          value={selected}
          label={label || 'Lists'}
          onChange={onChange}
          endAdornment={
            selected && (
              <InputAdornment position="end" className="mr-4">
                <IconButton
                  size="small"
                  onClick={handleClearSelection}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
          renderValue={(v) => {
            const list = filteredLists?.find((l) => l.publicId === v);
            if (list) {
              return list.name;
            }
            const team = filteredTeams?.find((t) => t.id === Number(v));
            if (team) {
              return team.name;
            }
            if (router.query.list) {
              const listValues = slugToValues(router.query.list as string);
              return listValues.label || 'List';
            }
            const project = filteredProjects?.find((p) => p.publicId === v);
            if (project) {
              return project.name;
            }
            return '';
          }}
        >
          {totalItems > SHOW_FILTER_ITEMS_MIN && (
            <div className="p-2 sticky top-0 bg-white z-10">
              <TextField
                size="small"
                fullWidth
                placeholder="Filter lists, teams, or projects..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                InputProps={{
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSearchQuery('');
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          )}
          <MenuItem value="">All</MenuItem>
          <MenuItem key={favoritesList?.id} value={favoritesList?.publicId}>
            {favoritesList?.name}
          </MenuItem>
          {(filteredTeamsBySearch?.length || 0) > 0 && (
            <div>
              <Divider />
              <div className="px-3 py-1 text-sm font-semibold text-gray-500">TEAMS</div>
            </div>
          )}
          {filteredTeamsBySearch?.map((t) => (
            <MenuItem key={`team-${t.id}`} value={`team-${t.id}`}>
              <div className="gap-2">
                <div className="font-semibold">
                  {t.name}
                </div>
                <div className="text-xs gap-2">
                  Team
                  {' '}
                  {pluralize('account', t.accounts?.length || 0)}
                </div>
              </div>
            </MenuItem>
          ))}
          {(filteredListsBySearch?.length || 0) > 0 && (
            <div>
              <Divider />
              <div className="px-3 py-1 text-sm font-semibold text-gray-500">LISTS</div>
            </div>
          )}
          {filteredListsBySearch?.map((l) => {
            const teamName = filteredTeams?.find((t) => t.id === l.teamId)?.name;
            return (
              <MenuItem key={l.id} value={l.publicId}>
                <div>
                  <div className="font-semibold">{l.name}</div>
                  <div className="text-xs text-gray-600 flex items-center gap-2">
                    {convertZuluDate(l.createdAt)}
                    {teamName && (
                      <div>
                        {' '}
                        {teamName}
                      </div>
                    )}
                    {l.jobId && (
                      <div>
                        <div className="bg-alpineTeal px-2 rounded font-bold text-white">job</div>
                      </div>
                    )}
                  </div>
                </div>
              </MenuItem>
            );
          })}
          {(!!filteredProjectsBySearch?.length) && (
            <div>
              <Divider />
              <div className="px-3 py-1 text-sm font-semibold text-gray-500">PROJECTS</div>
            </div>
          )}
          {filteredProjectsBySearch?.map((p) => {
            const teamName = filteredTeams?.find((t) => t.id === p.teamId)?.name;
            return (
              <MenuItem key={p.publicId || p.id} value={p.publicId || p.id}>
                <div>
                  <div className="font-semibold">{p.name}</div>
                  {teamName && (
                    <div className="text-xs text-gray-600">{teamName}</div>
                  )}
                </div>
              </MenuItem>
            );
          })}
          <Divider />
          <MenuItem value="add new" onClick={openAddList}>Create a New List</MenuItem>
          {(!isGondolaApp || !userIsProOrAdmin(currentUser)) && <MenuItem value="manage" onClick={() => router.push('/lists')}>Manage Lists</MenuItem>}
        </Select>
      </FormControl>
    </div>
  );
};
